import * as React from 'react';
import { includes, isEqual, pull } from 'lodash';
import { Subtract } from 'utility-types';

export interface IComponentsOverlay extends Window {
    overlayScrollbarWidth: number;
    overlayOverlays: string[];
    overlaySavedScrollPos: number;
}

declare const window: IComponentsOverlay;

export interface IWithOverlaysProps {
    /** HOC prop - list of opened overlays */
    overlays: string[];
    /** HOC prop - scrollbar width */
    scrollbarWidth: number;

    /** HOC prop - function to open a new overlay */
    openOverlay (name: string, asNew: boolean): void;

    /** HOC prop - function to close overlay(s) */
    closeOverlay (name?: string, instant?: boolean, noScrollRestore?: boolean): void;

}

interface IState {
    overlays: string[];
    scrollbarWidth: number;
    savedScrollPos: number;
}

const doc = document.documentElement;

/**
 * Extends props with currently opened overlays array, scrollbarWidth, openOverlay and closeOerlay props. See source code for details.
 */
export const withOverlays = <Props extends IWithOverlaysProps,
    ComponentProps = Subtract<Props, IWithOverlaysProps>,
    > (
    Component: React.Component<Props> | React.ComponentClass<Props> | React.SFC<Props>
) => (
    class WithOverlays extends React.Component<ComponentProps, IState> {
        static displayName: string = 'WithOverlays';
        private openingScrollbars = this.handleOpeningScrollbars.bind(this);
        private closingScrollbars = this.handleClosingScrollbars.bind(this);
        private handlingSync = this.handleStateSync.bind(this);
        private handlingResize = this.handleResize.bind(this);

        constructor (props) {
            super(props);
            this.state = {
                overlays: window.overlayOverlays ? [...window.overlayOverlays] : [],
                scrollbarWidth: window.overlayScrollbarWidth ? window.overlayScrollbarWidth : 0,
                savedScrollPos: window.overlaySavedScrollPos ? window.overlaySavedScrollPos : 0,
            };
            if (!window.overlayOverlays) {
                window.overlayOverlays = [];
            }
            if (!window.overlaySavedScrollPos) {
                window.overlaySavedScrollPos = 0;
            }
        }

        handleOpeningScrollbars () {
            if (doc) {
                let scrollwidth = window.innerWidth - doc.clientWidth;
                if (window.overlayScrollbarWidth && window.overlayScrollbarWidth > scrollwidth) {
                    scrollwidth = window.overlayScrollbarWidth;
                }
                if (scrollwidth > 0) {
                    window.overlayScrollbarWidth = scrollwidth;
                    window.dispatchEvent(new CustomEvent('lqa:overlay:sync'));
                }
            }
        }

        handleClosingScrollbars () {
            if (doc && !doc.classList.contains('overlayopen')) {
                window.overlayScrollbarWidth = 0;
                window.dispatchEvent(new CustomEvent('lqa:overlay:sync'));
            }
        }

        handleResize () {
            if (doc && this.state.overlays.length > 0) {
                const root = document.getElementById('root');
                if (root) {
                    root.style.height = doc.clientHeight + 'px';
                    root.style.minHeight = doc.clientHeight + 'px';
                }
                doc.style.height = doc.clientHeight + 'px';
            }
        }

        handleOpen = (name: string, asNew: boolean) => {
            if (includes(this.state.overlays, name)) {
                this.handleClose(name);
            } else {
                const scroller = document.getElementById('root');
                if (doc) {
                    const pos = doc.scrollTop || document.body.scrollTop;
                    window.dispatchEvent(new CustomEvent('lqa:overlay:open'));

                    this.handleResize();

                    if (this.state.overlays.length === 0) {
                        // Scroll to top
                        window.scrollTo(0, 0);

                        // Clip window to size
                        doc.classList.add('overlayopen');

                        // Cache scroll position
                        window.overlaySavedScrollPos = pos;
                        this.setState({
                            savedScrollPos: pos,
                        });

                        window.dispatchEvent(new CustomEvent('lqa:overlay:sync'));

                        window.requestAnimationFrame(() => {
                            if (scroller) {
                                scroller.scrollTop = pos;
                            }
                        });
                    }
                    if (asNew) {
                        const arr = [...this.state.overlays];
                        arr.push(name);
                        this.setState({
                            overlays: arr,
                        }, () => {
                            window.overlayOverlays = arr;
                            window.dispatchEvent(new CustomEvent('lqa:overlay:sync'));
                        });
                    } else {
                        const arr: string[] = [];
                        arr.push(name);
                        this.setState({
                            overlays: arr,
                        }, () => {
                            window.overlayOverlays = arr;
                            window.dispatchEvent(new CustomEvent('lqa:overlay:sync'));
                        });
                    }
                }
            }
        };

        handleClose = (name?: string, instant?: boolean, noScrollRestore?: boolean) => {
            let arr = [...this.state.overlays];
            if (doc) {
                if (arr.length > 0) {
                    if (name === 'all') {
                        arr = [];
                    } else if (name) {
                        pull(arr, name);
                    } else {
                        arr.splice(-1, 1);
                    }
                    window.overlayOverlays = arr;
                    window.dispatchEvent(new CustomEvent('lqa:overlay:sync'));
                    if (doc && window.overlayOverlays.length === 0) {
                        this.closeOverlayCleanup(false, noScrollRestore);
                    }
                } else if (name === 'all') {
                    this.closeOverlayCleanup(instant, noScrollRestore);
                }
            }
        };

        closeOverlayCleanup = (instant?: boolean, noScrollRestore?: boolean) => {
            if (doc) {
                doc.style.height = '';
                const root = document.getElementById('root');
                if (root) {
                    root.style.height = '';
                    root.style.minHeight = '';
                }
                doc.classList.remove('overlayopen');
                if (!noScrollRestore && this.state.savedScrollPos > 0) {
                    document.body.scrollTop = this.state.savedScrollPos;
                    doc.scrollTop = this.state.savedScrollPos;
                    window.requestAnimationFrame(() => {
                        document.body.scrollTop = this.state.savedScrollPos;
                        doc.scrollTop = this.state.savedScrollPos;
                    });
                }
                if (instant) {
                    window.dispatchEvent(new CustomEvent('lqa:overlay:close'));
                } else {
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('lqa:overlay:close'));
                    }, 250);
                }
            }
        };

        handleStateSync () {
            if (!isEqual(this.state.overlays, window.overlayOverlays)) {
                this.setState({
                    overlays: [...window.overlayOverlays],
                });
            }
            if (
                this.state.scrollbarWidth !== window.overlayScrollbarWidth ||
                this.state.savedScrollPos !== window.overlaySavedScrollPos
            ) {
                this.setState({
                    scrollbarWidth: window.overlayScrollbarWidth,
                    savedScrollPos: window.overlaySavedScrollPos,
                });
            }
        }

        componentDidMount () {
            window.addEventListener('lqa:overlay:open', this.openingScrollbars);
            window.addEventListener('lqa:overlay:close', this.closingScrollbars);
            window.addEventListener('lqa:overlay:sync', this.handlingSync);
            window.addEventListener('resize', this.handlingResize);
        }

        componentWillUnmount () {
            window.removeEventListener('lqa:overlay:open', this.openingScrollbars);
            window.removeEventListener('lqa:overlay:close', this.closingScrollbars);
            window.removeEventListener('lqa:overlay:sync', this.handlingSync);
            window.removeEventListener('resize', this.handlingResize);
        }

        render () {
            return (
                // @ts-ignore
                <Component
                    {...this.props}
                    overlays={this.state.overlays}
                    openOverlay={this.handleOpen}
                    closeOverlay={this.handleClose}
                    scrollbarWidth={this.state.scrollbarWidth}
                />
            );
        }
    }
);
